import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Users, TrendingUp, Wallet, Megaphone, FileText, ShoppingBag, Target, Mail, Palette } from 'lucide-react';
import { PillPrimary, PillOutline } from '../components/PillButton';
import { heroImages, stats } from '../mock';
import { api, resolveImage } from '../lib/api';

const SplitSection = ({ imgLeft = true, image, tagline, title, body, ctas, dark = false }) => (
  <section className={`grid grid-cols-1 lg:grid-cols-2 ${dark ? 'bg-[#1e1e1e]' : 'bg-[#3a3a3a]'}`}>
    {imgLeft && <div className="h-[420px] lg:h-[620px] bg-cover bg-center order-1 lg:order-1" style={{ backgroundImage: `url(${image})` }} />}
    <div className="flex items-center px-8 md:px-14 lg:px-20 py-16 lg:py-24 order-2">
      <div className="max-w-xl">
        {tagline && (
          <div className="inline-block px-5 py-2.5 rounded-full border border-[#e9dfc0]/40 bg-[#2b2b2b]/40 text-[#e9dfc0] text-[13px] mb-8">{tagline}</div>
        )}
        <h2 className="font-serif text-white text-4xl md:text-5xl leading-[1.1] mb-6">{title}</h2>
        <p className="text-[#e9dfc0]/85 text-[15px] md:text-[16px] leading-relaxed mb-8">{body}</p>
        {ctas && <div className="flex flex-wrap gap-3">{ctas}</div>}
      </div>
    </div>
    {!imgLeft && <div className="h-[420px] lg:h-[620px] bg-cover bg-center order-1 lg:order-3" style={{ backgroundImage: `url(${image})` }} />}
  </section>
);

const Home = () => {
  const [interesse, setInteresse] = useState('');
  const [form, setForm] = useState({ name: '', email: '', phone: '', comment: '' });
  const [submitted, setSubmitted] = useState(false);
  const [caseStudies, setCaseStudies] = useState([]);

  useEffect(() => {
    api.listCaseStudies().then((d) => setCaseStudies(d.slice(0, 3))).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.createLead({
        source: 'home',
        name: form.name,
        email: form.email,
        phone: form.phone,
        interest: interesse,
        message: form.comment,
      });
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 4000);
      setForm({ name: '', email: '', phone: '', comment: '' });
      setInteresse('');
    } catch (err) {
      alert('Errore nell\u2019invio. Riprova.');
    }
  };

  return (
    <div className="bg-[#2b2b2b]">
      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-[#141414]">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h1 className="font-serif text-white text-5xl md:text-7xl leading-none mb-6">ETech</h1>
          <p className="font-serif text-white text-3xl md:text-5xl leading-tight">Il tuo eCommerce, senza complicazioni.</p>
        </div>
      </section>

      {/* TM Banner */}
      <section className="bg-[#e9dfc0] py-6">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-[#2b2b2b]" />
            <p className="text-[#2b2b2b] text-[15px] md:text-[16px]">
              <span className="font-semibold">Nasce ETech Temporary Management</span> — il nostro team entra nella tua azienda e cresce con te, anche investendo insieme a te.
            </p>
          </div>
          <Link to="/temporary-management" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#2b2b2b] text-[#e9dfc0] text-sm font-medium hover:bg-[#1e1e1e] transition-colors whitespace-nowrap">
            Scopri di più <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Dove interveniamo */}
      <section className="bg-[#2b2b2b] py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="inline-block px-5 py-2.5 rounded-full border border-[#e9dfc0]/40 bg-[#1a1a1a]/40 text-[#e9dfc0] text-[13px] mb-8">
            L'eCommerce è complesso. Noi lo rendiamo semplice.
          </div>
          <h2 className="font-serif text-white text-4xl md:text-5xl mb-6">Dove interveniamo</h2>
          <p className="text-[#e9dfc0]/75 text-[16px] md:text-[17px] leading-relaxed max-w-3xl mb-14">
            Da oltre 7 anni operiamo nell'eCommerce non come consulenti, ma come imprenditori diretti: brand propri lanciati, progetti di terzi gestiti, capitale nostro investito in ogni fase del percorso.<br />
            La nostra missione: trasformare la complessità del commercio online in un sistema scalabile, replicabile e profittevole.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Temporary Management (nuovo) */}
            <div className="bg-[#3a3a3a] border border-[#e9dfc0]/30 rounded-2xl p-8 hover:border-[#e9dfc0]/60 transition-all group">
              <div className="w-12 h-12 rounded-full bg-[#e9dfc0] text-[#2b2b2b] flex items-center justify-center mb-6">
                <Users className="w-5 h-5" />
              </div>
              <div className="inline-block px-3 py-1 rounded-full bg-[#e9dfc0] text-[#2b2b2b] text-[11px] font-semibold mb-4">NEW</div>
              <h3 className="font-serif text-white text-2xl mb-4">Temporary Management</h3>
              <p className="text-[#e9dfc0]/85 text-[15px] leading-relaxed mb-6">Il nostro team entra nella tua azienda e gestisce marketing, ads, sito, logistica e amministrazione come se fosse un reparto interno — in alcuni casi condividendo anche il rischio d'impresa.</p>
              <Link to="/temporary-management" className="inline-flex items-center gap-2 text-[#e9dfc0] text-sm font-medium group-hover:gap-3 transition-all">
                Scopri di più <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="bg-[#3a3a3a] border border-[#e9dfc0]/20 rounded-2xl p-8 hover:border-[#e9dfc0]/50 transition-all group">
              <div className="w-12 h-12 rounded-full bg-[#e9dfc0]/20 text-[#e9dfc0] flex items-center justify-center mb-6">
                <TrendingUp className="w-5 h-5" />
              </div>
              <h3 className="font-serif text-white text-2xl mb-4">Analisi e ottimizzazione logistica</h3>
              <p className="text-[#e9dfc0]/85 text-[15px] leading-relaxed mb-6">Non ci occupiamo direttamente di logistica, ma affianchiamo le aziende nella definizione della strategia logistica più efficiente, individuando KPI, colli di bottiglia, costi occulti e opportunità di miglioramento.</p>
              <Link to="/pocket-administration" className="inline-flex items-center gap-2 text-[#e9dfc0] text-sm font-medium group-hover:gap-3 transition-all">
                Scopri di più <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="bg-[#3a3a3a] border border-[#e9dfc0]/20 rounded-2xl p-8 hover:border-[#e9dfc0]/50 transition-all group">
              <div className="w-12 h-12 rounded-full bg-[#e9dfc0]/20 text-[#e9dfc0] flex items-center justify-center mb-6">
                <Wallet className="w-5 h-5" />
              </div>
              <h3 className="font-serif text-white text-2xl mb-4">Consulenza amministrativa per eCommerce</h3>
              <p className="text-[#e9dfc0]/85 text-[15px] leading-relaxed mb-6">Supportiamo le imprese nella gestione amministrativa legata al mondo digitale: contabilità, fiscalità specifica, gestione dei flussi e compliance normativa.</p>
              <Link to="/pocket-administration" className="inline-flex items-center gap-2 text-[#e9dfc0] text-sm font-medium group-hover:gap-3 transition-all">
                Scopri di più <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Casi Studio anteprima */}
      <section className="bg-[#2b2b2b] py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row items-start md:items-end justify-between mb-12 gap-6">
            <div className="max-w-2xl">
              <div className="inline-block px-5 py-2.5 rounded-full border border-[#e9dfc0]/40 text-[#e9dfc0] text-[13px] mb-6">Prova sociale</div>
              <h2 className="font-serif text-white text-4xl md:text-5xl mb-4">Casi studio</h2>
              <p className="text-[#e9dfc0]/70 text-[15px] md:text-[16px] leading-relaxed">
                Risultati concreti dai nostri brand e dai progetti che gestiamo per conto terzi. Ogni caso racconta punti di partenza reali, azioni misurabili e KPI verificati.
              </p>
            </div>
            <PillOutline to="/casi-studio">Vedi tutti</PillOutline>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {caseStudies.slice(0, 3).map((cs) => (
              <Link key={cs.slug} to={`/casi-studio/${cs.slug}`} className="group">
                <div className="aspect-[4/3] bg-cover bg-center rounded-xl mb-4 overflow-hidden">
                  <div className="w-full h-full bg-cover bg-center group-hover:scale-105 transition-transform duration-500" style={{ backgroundImage: `url(${resolveImage(cs.image)})` }} />
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[#e9dfc0] font-serif text-2xl">{cs.metric}</span>
                  <span className="text-[#e9dfc0]/60 text-sm">· {cs.time}</span>
                </div>
                <h3 className="text-white font-serif text-xl leading-snug mb-2 group-hover:text-[#e9dfc0] transition-colors">{cs.title}</h3>
                <p className="text-[#e9dfc0]/70 text-sm">{cs.excerpt}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Servizi eCommerce */}
      <section className="bg-[#1e1e1e] py-24" data-testid="servizi-ecommerce-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-14">
            <div className="inline-block px-5 py-2.5 rounded-full border border-[#e9dfc0]/40 bg-[#2b2b2b]/40 text-[#e9dfc0] text-[13px] mb-6">
              Servizi eCommerce a 360°
            </div>
            <h2 className="font-serif text-white text-4xl md:text-5xl mb-6">
              Tutto ciò che serve al tuo shop online, sotto un unico tetto.
            </h2>
            <p className="text-[#e9dfc0]/80 text-[16px] md:text-[17px] leading-relaxed">
              Dalla pianificazione strategica alle campagne pubblicitarie, dall'ottimizzazione del catalogo alla gestione delle conversioni: ci occupiamo di ogni aspetto del tuo eCommerce con un approccio integrato, misurabile e orientato al ROI. Ogni servizio è pensato per far crescere il tuo brand in modo scalabile.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Megaphone,
                title: 'Campagne Ads',
                body: 'Gestione professionale di Google Ads, Meta Ads e TikTok Ads con reportistica trasparente e ottimizzazione continua del ROAS.',
              },
              {
                icon: FileText,
                title: 'Documento strategico',
                body: 'Piano operativo su misura con analisi di mercato, positioning, KPI target e roadmap trimestrale per la crescita.',
              },
              {
                icon: ShoppingBag,
                title: 'Gestione catalogo',
                body: 'Ottimizzazione schede prodotto, categorizzazione, foto e copywriting orientati alla conversione e al posizionamento SEO.',
              },
              {
                icon: Target,
                title: 'CRO & Analytics',
                body: 'A/B test, heatmap e ottimizzazione del funnel per aumentare tasso di conversione e valore medio del carrello.',
              },
              {
                icon: Mail,
                title: 'Email & CRM',
                body: 'Automation, flussi post-acquisto, newsletter segmentate e strategie di retention per aumentare il LTV cliente.',
              },
              {
                icon: Palette,
                title: 'Creatività & Content',
                body: 'Produzione di creatività ADV, contenuti social e materiali visual coerenti con il brand e performanti sulle piattaforme.',
              },
            ].map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                data-testid={`servizio-card-${title.toLowerCase().replace(/\s|&/g, '-')}`}
                className="bg-[#2b2b2b] border border-[#e9dfc0]/20 rounded-2xl p-8 hover:border-[#e9dfc0]/60 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-full bg-[#e9dfc0]/15 text-[#e9dfc0] flex items-center justify-center mb-6">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-serif text-white text-2xl mb-3">{title}</h3>
                <p className="text-[#e9dfc0]/80 text-[15px] leading-relaxed">{body}</p>
              </div>
            ))}
          </div>

          <div className="mt-14 flex flex-wrap justify-center gap-3">
            <PillPrimary to="/contatti">Richiedi una consulenza</PillPrimary>
            <PillOutline to="/chi-siamo">Scopri il metodo</PillOutline>
          </div>
        </div>
      </section>

      {/* Split 1 - Strategia */}
      <SplitSection
        imgLeft={false}
        image={heroImages.homeHero}
        tagline="Dietro ogni clic, una strategia vincente."
        title="Consulenza, strategia, logistica: il motore del tuo shop online."
        body="Siamo un'azienda specializzata nell'eCommerce a 360°, offrendo soluzioni su misura sia per i nostri brand che per conto terzi. Accompagniamo i nostri clienti in ogni fase del percorso digitale, dall'impostazione strategica alla gestione operativa."
        ctas={<>
          <PillPrimary to="/chi-siamo">Chi siamo</PillPrimary>
          <PillOutline to="/contatti">Contattaci</PillOutline>
        </>}
      />

      {/* Split 2 - Formazione */}
      <SplitSection
        imgLeft={true}
        dark
        image={heroImages.formazione}
        tagline="Non solo vendite online: costruiamo ecosistemi digitali."
        title="Formazione professionale su Shopify e Facebook Ads"
        body="Affianchiamo aziende, team interni e professionisti nella formazione completa su Shopify e Facebook Ads, con percorsi pratici, aggiornati e orientati ai risultati. Insegniamo come costruire e gestire uno store Shopify performante, integrandolo con strumenti essenziali per vendere online in modo efficace. L'obiettivo: rendere il cliente autonomo, consapevole e capace di gestire con successo la propria presenza digitale."
        ctas={<>
          <PillPrimary to="/sviluppo-brand">Scopri di più</PillPrimary>
          <PillOutline to="/contatti">Contattaci</PillOutline>
        </>}
      />

      {/* Form contatti */}
      <section className="bg-[#1e1e1e] py-24">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-serif text-white text-4xl md:text-5xl mb-3 text-center">Hai un'idea? Rendiamola realtà!</h2>
          <p className="text-[#e9dfc0]/70 text-center mb-12">Raccontaci il tuo progetto, ti rispondiamo entro 24 ore.</p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} type="text" placeholder="Nome" className="w-full bg-[#2b2b2b] border border-[#4a4a4a] text-white placeholder:text-[#e9dfc0]/50 rounded-lg py-4 px-5 focus:outline-none focus:border-[#e9dfc0] transition-colors" />
              <input required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" placeholder="Email*" className="w-full bg-[#2b2b2b] border border-[#4a4a4a] text-white placeholder:text-[#e9dfc0]/50 rounded-lg py-4 px-5 focus:outline-none focus:border-[#e9dfc0] transition-colors" />
            </div>
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} type="tel" placeholder="Phone" className="w-full bg-[#2b2b2b] border border-[#4a4a4a] text-white placeholder:text-[#e9dfc0]/50 rounded-lg py-4 px-5 focus:outline-none focus:border-[#e9dfc0] transition-colors" />
            <select required value={interesse} onChange={(e) => setInteresse(e.target.value)} className="w-full bg-[#2b2b2b] border border-[#4a4a4a] text-white rounded-lg py-4 px-5 focus:outline-none focus:border-[#e9dfc0] transition-colors">
              <option value="" disabled>Sono interessato a…</option>
              <option value="standard">Servizi eCommerce standard</option>
              <option value="tm">ETech Temporary Management</option>
            </select>
            <textarea value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} rows={5} placeholder="Comment" className="w-full bg-[#2b2b2b] border border-[#4a4a4a] text-white placeholder:text-[#e9dfc0]/50 rounded-lg py-4 px-5 focus:outline-none focus:border-[#e9dfc0] transition-colors resize-none" />
            <div className="flex items-center gap-4">
              <PillPrimary type="submit">Invia</PillPrimary>
              {submitted && <p className="text-[#e9dfc0] text-sm">Messaggio inviato! Ti rispondiamo a breve.</p>}
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Home;
